import { PLAN_CONFIGS } from '@inboxkit/contracts/plans';
import {
  ArrowDown,
  ArrowRight,
  AtSign,
  Check,
  Copy,
  FileSearch,
  Inbox,
  KeyRound,
  Paperclip,
  Plus,
  Search,
  Webhook,
} from 'lucide-react';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { HeroShader, SiteFooter, SiteHeader } from '@inboxkit/ui';
import { HeroDemo } from './HeroDemo';
import { track } from './lib/analytics';
import { useSiteUrls } from './lib/urls';

// CFO HARD GATE: every CTA goes to Request Access (lead capture), NOT public
// self-serve signup. Public trial BLOCKED until: outbound send metering +
// abuse floors + Domain Connect E2E.

const API_HOST = 'https://api.inboxkit.dev';
// The API origin redirects "/" to this site, so "Docs" points at the on-page
// API section; the push guide is the one hosted docs page.

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

/**
 * Adds `is-visible` to `.reveal` elements once they scroll into view, including
 * ones mounted later (conditional UI, hot reload). Returns the cleanup.
 */
function watchReveals(): () => void {
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      }
    },
    { threshold: 0.12, rootMargin: '0px 0px -40px 0px' },
  );
  const watch = (root: ParentNode) => {
    for (const el of root.querySelectorAll('.reveal:not(.is-visible)')) io.observe(el);
  };
  watch(document);
  const mo = new MutationObserver((records) => {
    for (const record of records) {
      for (const node of record.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('.reveal:not(.is-visible)')) io.observe(node);
        watch(node);
      }
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
  return () => {
    mo.disconnect();
    io.disconnect();
  };
}

function useReveal() {
  useEffect(() => watchReveals(), []);
}

function SectionTitle({ title, sub, className = '' }: { title: ReactNode; sub?: ReactNode; className?: string }) {
  return (
    <div className={`reveal mx-auto mb-16 max-w-3xl text-center ${className}`}>
      <h2 className="text-balance text-[42px] leading-[1.05] font-normal tracking-[-0.035em] text-ink sm:text-[56px]">
        {title}
      </h2>
      {sub && <p className="mx-auto mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-mute">{sub}</p>}
    </div>
  );
}

function InkButton({ href, label, children }: { href: string; label: string; children: ReactNode }) {
  return (
    <a
      href={href}
      onClick={() => track('cta_click', { label })}
      className="ink-btn group inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-transform active:scale-[0.98]"
    >
      {children}
      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
    </a>
  );
}

function GlassButton({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      className="glass inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium text-ink transition-transform active:scale-[0.98]"
    >
      {children}
    </a>
  );
}

function CopyButton({ text, dark = false }: { text: string; dark?: boolean }) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(text).then(() => {
          setCopied(true);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(() => setCopied(false), 1400);
        });
      }}
      className={`grid h-7 w-7 shrink-0 place-items-center rounded-md transition-colors ${
        dark ? 'text-white/40 hover:bg-white/[0.07] hover:text-white' : 'text-mute hover:bg-ink/[0.05] hover:text-ink'
      }`}
      aria-label={copied ? 'Copied' : 'Copy code'}
    >
      {copied ? <Check className="h-3.5 w-3.5 text-leaf" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Tiny highlighter: comments, strings, keywords
// ---------------------------------------------------------------------------

const KEYWORDS =
  /\b(const|await|async|new|return|import|from|def|if|for|in|export|function|True|False|None|curl)\b/;

const THEMES = {
  light: { com: 'text-code-com', str: 'text-code-str', kw: 'text-code-kw' },
  dark: { com: 'text-white/35', str: 'text-syntax-str', kw: 'text-syntax-kw' },
};

function highlight(code: string, theme: keyof typeof THEMES, numbered = false): ReactNode[] {
  const c = THEMES[theme];
  return code.split('\n').map((line, i) => {
    const num = numbered && (
      <span className="mr-5 inline-block w-5 select-none text-right text-code-num">{i + 1}</span>
    );
    if (/^\s*(#|\/\/)/.test(line)) {
      return (
        // biome-ignore lint/suspicious/noArrayIndexKey: static snippet lines
        <div key={i} className={c.com}>
          {num}
          {line}
        </div>
      );
    }
    return (
      // biome-ignore lint/suspicious/noArrayIndexKey: static snippet lines
      <div key={i}>
        {num}
        {line.split(/("[^"]*"|'[^']*'|`[^`]*`)/g).map((part, j) =>
          /^["'`]/.test(part) ? (
            // biome-ignore lint/suspicious/noArrayIndexKey: static tokens
            <span key={j} className={c.str}>
              {part}
            </span>
          ) : (
            // KEYWORDS has one capture group, so keywords land on odd indexes.
            part.split(KEYWORDS).map((t, k) =>
              k % 2 === 1 ? (
                // biome-ignore lint/suspicious/noArrayIndexKey: static tokens
                <span key={`${j}-${k}`} className={c.kw}>
                  {t}
                </span>
              ) : (
                t
              ),
            )
          ),
        )}
        {line === '' && ' '}
      </div>
    );
  });
}

// ---------------------------------------------------------------------------
// Nav + hero
// ---------------------------------------------------------------------------

type QuickLang = 'curl' | 'fetch' | 'python';

const QUICK_LABEL: Record<QuickLang, string> = { curl: 'cURL', fetch: 'TypeScript', python: 'Python' };

const QUICK: Record<QuickLang, string> = {
  curl: `curl -X POST ${API_HOST}/v1/inboxes \\
  -H "Authorization: Bearer $INBOXKIT_API_KEY"`,
  fetch: `await fetch("${API_HOST}/v1/inboxes", {
  method: "POST", headers: { Authorization: \`Bearer \${key}\` } })`,
  python: `requests.post("${API_HOST}/v1/inboxes",
  headers={"Authorization": f"Bearer {key}"})`,
};

function QuickStart() {
  const [lang, setLang] = useState<QuickLang>('curl');
  return (
    <div className="mt-9 max-w-[520px]">
      <div className="flex items-center gap-2 text-[14px]">
        {(Object.keys(QUICK) as QuickLang[]).map((l, i) => (
          <span key={l} className="flex items-center gap-2">
            {i > 0 && <span className="text-ink/30">·</span>}
            <button
              type="button"
              onClick={() => setLang(l)}
              aria-pressed={lang === l}
              className={`transition-colors ${lang === l ? 'text-ink' : 'text-ink/45 hover:text-ink/75'}`}
            >
              {QUICK_LABEL[l]}
            </button>
          </span>
        ))}
      </div>
      <div className="relative mt-3 h-px w-full overflow-hidden bg-ink/10">
        <div className="spectrum absolute inset-y-0 left-0 w-2/3 opacity-80" />
      </div>
      <div className="mt-3 flex items-start gap-3">
        <code
          key={lang}
          className="animate-fade-up block min-w-0 flex-1 font-mono text-[11px] leading-6 whitespace-pre-wrap text-ink-soft dark:text-ink [overflow-wrap:anywhere] sm:text-[12.5px]"
        >
          {QUICK[lang]}
        </code>
        <CopyButton text={QUICK[lang]} />
      </div>
      <p className="mt-2 font-mono text-[11.5px] text-mute dark:text-ink-soft">
        → <span className="text-ink-soft">agent_7kq2@k7f3.inboxkit.dev</span> is live. No DNS.
      </p>
    </div>
  );
}

function Hero({ requestAccessUrl }: { requestAccessUrl: string }) {
  const anchor = useRef<HTMLDivElement>(null);
  return (
    <section className="hero-paper relative isolate min-h-[100svh] overflow-hidden">
      <HeroShader anchor={anchor} className="absolute inset-0 -z-10 h-full w-full" />
      {/* Dark theme: a soft ink scrim keeps text readable where the spectrum crosses it. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 left-0 hidden w-[62%] bg-[radial-gradient(ellipse_70%_55%_at_24%_66%,rgb(11_12_15/0.88),rgb(11_12_15/0.5)_45%,transparent_75%)] dark:block"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-b from-transparent to-paper md:h-48"
      />

      <div className="mx-auto grid min-h-[100svh] max-w-[1400px] items-center px-6 pt-32 pb-20 md:grid-cols-[minmax(0,560px)_1fr] md:gap-10 md:pt-20 lg:px-10">
        <div className="min-w-0">
          <a
            href={requestAccessUrl}
            onClick={() => track('cta_click', { label: 'hero_badge' })}
            className="glass animate-fade-up inline-flex items-center gap-2 rounded-full py-1 pr-3 pl-1.5 text-[12px] text-ink-soft"
          >
            <span className="spectrum h-4 w-4 rounded-full" />
            Private beta · onboarding in small batches
            <ArrowRight className="h-3 w-3" aria-hidden />
          </a>

          <h1
            className="animate-fade-up mt-8 font-serif text-[96px] leading-[0.86] font-normal tracking-[-0.02em] text-ink sm:text-[148px]"
            style={{ animationDelay: '80ms' }}
          >
            InboxKit
          </h1>
          <p
            className="animate-fade-up mt-6 text-[30px] leading-[1.16] font-light tracking-[-0.025em] text-ink sm:text-[40px]"
            style={{ animationDelay: '160ms' }}
          >
            Email inboxes,
            <br />
            designed for <span className="font-serif text-[1.12em] italic">agents</span>.
          </p>

          <div className="animate-fade-up" style={{ animationDelay: '240ms' }}>
            <QuickStart />
          </div>

          <div className="animate-fade-up mt-9 flex flex-wrap items-center gap-3" style={{ animationDelay: '320ms' }}>
            <InkButton href={requestAccessUrl} label="hero">
              Request access
            </InkButton>
            <GlassButton href="#how">See how it works</GlassButton>
          </div>
        </div>
        {/* Where the glass envelope floats. The shader reads this box. */}
        <div ref={anchor} aria-hidden className="mt-10 h-[240px] w-full md:mt-0 md:h-[440px]" />
      </div>

      <a
        href="#flow"
        className="absolute bottom-8 left-1/2 hidden -translate-x-1/2 items-center gap-2 font-mono text-[10.5px] tracking-[0.2em] text-ink/45 uppercase transition-colors hover:text-ink md:flex"
      >
        <ArrowDown className="h-3 w-3 animate-bounce" aria-hidden />
        Scroll
      </a>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Manifesto: words fill with ink as you scroll through them
// ---------------------------------------------------------------------------

// *word* renders in serif italic.
const MANIFESTO =
  'Agents can browse, write code, and call any API. But the world still runs on email — sign-ups, invoices, support, introductions. InboxKit gives every agent *a* *real* *address,* so it can take part.';

function Manifesto() {
  const ref = useRef<HTMLParagraphElement>(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setProgress(1);
      return;
    }
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vh = window.innerHeight;
      // 0 when the text enters the lower part of the screen, 1 when it reaches the upper third.
      setProgress(Math.min(1, Math.max(0, (vh * 0.9 - r.top) / (r.height + vh * 0.45))));
    };
    const onScroll = () => {
      if (!raf) raf = requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  const words = MANIFESTO.split(' ');
  return (
    <section className="px-6 pt-40 pb-44">
      <p
        ref={ref}
        className="mx-auto max-w-5xl text-balance text-[34px] leading-[1.12] font-normal tracking-[-0.035em] text-ink sm:text-[58px]"
      >
        {words.map((raw, i) => {
          const serif = raw.startsWith('*');
          const word = raw.replaceAll('*', '');
          const t = Math.min(1, Math.max(0, progress * words.length * 1.2 - i));
          return (
            <span
              // biome-ignore lint/suspicious/noArrayIndexKey: fixed text
              key={i}
              className={`transition-opacity duration-300 ${serif ? 'font-serif text-[1.12em] italic' : ''}`}
              style={{ opacity: 0.13 + 0.87 * t }}
            >
              {word}{' '}
            </span>
          );
        })}
      </p>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Flow: one inbox fans out to four channels
// ---------------------------------------------------------------------------

function TileFrame({ label, meta, children }: { label: string; meta: string; children: ReactNode }) {
  return (
    <div className="sheet overflow-hidden rounded-xl transition-transform duration-300 hover:-translate-y-1">
      <div className="flex items-center justify-between gap-2 px-3.5 py-2.5 font-mono text-[10px] tracking-[0.14em] uppercase">
        <span className="text-ink">{label}</span>
        <span className="truncate text-mute">{meta}</span>
      </div>
      <div className="screen relative mx-1.5 mb-1.5 h-[170px] overflow-hidden rounded-lg text-white/55">{children}</div>
    </div>
  );
}

const WEBHOOK_LOG = [
  ['→', 'POST /hooks/mail', 'text-white/70'],
  [' ', 'X-InboxKit-Event: message.received', 'text-white/40'],
  [' ', 'X-InboxKit-Signature: sha256=…', 'text-white/40'],
  ['✓', 'signature verified', 'text-signal'],
  ['←', '200 OK', 'text-signal-dim'],
] as const;

function WebhookTile() {
  const rows = [...WEBHOOK_LOG, ...WEBHOOK_LOG, ...WEBHOOK_LOG, ...WEBHOOK_LOG];
  return (
    <TileFrame label="Webhook" meta="HMAC · SHA-256">
      <div className="absolute inset-0 [mask-image:linear-gradient(transparent,#000_25%,#000_75%,transparent)]">
        <div className="ticker px-3.5 font-mono text-[10.5px] leading-[22px]">
          {rows.map(([mark, text, cls], i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: repeated static log
            <div key={i} className={`truncate ${cls}`}>
              <span className="mr-2 text-white/30">{mark}</span>
              {text}
            </div>
          ))}
        </div>
      </div>
    </TileFrame>
  );
}

function SocketTile({ name }: { name: string }) {
  return (
    <TileFrame label="WebSocket" meta="wss · /v1/ws">
      <div className="absolute inset-0 flex flex-col justify-center gap-2 px-3.5 font-mono text-[10.5px]">
        <div className="text-white/40">
          <span className="text-syntax-kw">subscribe</span> <span className="text-white/70">{name}</span>@k7f3…
        </div>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            className="seq flex items-center gap-2 rounded-md border border-line bg-white/[0.03] px-2 py-1.5"
            style={seq(0.3 + i * 0.35)}
          >
            <span className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-signal" />
            <span className="text-syntax-str">message.received</span>
          </div>
        ))}
      </div>
    </TileFrame>
  );
}

function ThreadTile() {
  return (
    <TileFrame label="Thread" meta="In-Reply-To">
      <div className="absolute inset-0 flex flex-col justify-center gap-2 px-3.5 text-[11px]">
        <div className="max-w-[85%] rounded-lg rounded-bl-sm bg-white/[0.07] px-2.5 py-1.5 text-white/75">
          Charged twice for March?
        </div>
        <div className="ml-auto max-w-[85%] rounded-lg rounded-br-sm bg-signal/15 px-2.5 py-1.5 text-signal">
          Refunded the duplicate. 3–5 days.
        </div>
        <div className="max-w-[85%] rounded-lg rounded-bl-sm bg-white/[0.07] px-2.5 py-1.5 text-white/75">
          Perfect, thank you!
        </div>
      </div>
    </TileFrame>
  );
}

function SearchTile() {
  const results = ['Invoice #1042 — paid twice?', 'Refund for order 88-311', 'March billing question'];
  return (
    <TileFrame label="Search" meta="Semantic · /v1/search">
      <div className="absolute inset-0 flex flex-col justify-center gap-1.5 px-3.5 font-mono text-[10.5px]">
        <div className="mb-1 flex items-center gap-2 rounded-md border border-line-strong bg-white/[0.04] px-2 py-1.5 text-white/85">
          <Search className="h-3 w-3 text-white/40" aria-hidden />
          <span className="caret">refund requests</span>
        </div>
        {results.map((r) => (
          <div key={r} className="flex items-center gap-2 truncate text-white/55">
            <span className="text-signal-dim">›</span>
            {r}
          </div>
        ))}
      </div>
    </TileFrame>
  );
}

function Connector({ className = '' }: { className?: string }) {
  return (
    <div className={`relative w-px overflow-hidden bg-ink/15 ${className}`}>
      <div className="pulse-down absolute inset-x-0 top-0 h-1/3 bg-gradient-to-b from-transparent via-ink/70 to-transparent" />
    </div>
  );
}

const HOSTED = 'k7f3.inboxkit.dev';

/** Same rule as the API: 1–64 chars, lowercase letters, digits, and . _ + - */
function toLocalPart(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9._+-]/g, '')
    .slice(0, 32);
}

const flowCode = (name: string) => `const res = await fetch("${API_HOST}/v1/inboxes", {
  method: "POST",
  headers: { Authorization: \`Bearer \${key}\` },
  body: JSON.stringify({ local_part: "${name}" }),
});
// ${name}@${HOSTED} — reachable four ways`;

function Flow() {
  const [raw, setRaw] = useState('support');
  const name = toLocalPart(raw) || 'agent';
  return (
    <section id="flow" className="scroll-mt-16 px-6 pb-36">
      <SectionTitle
        title="One inbox. Every way in and out."
        sub="Create an address once. New mail reaches your agent the way it prefers, and replies land back in the same thread."
      />
      <div className="reveal relative mx-auto max-w-5xl">
        <label className="glass mx-auto mb-5 flex w-fit items-center gap-3 rounded-full py-1.5 pr-4 pl-4 text-[13px] text-ink-soft">
          <span className="text-mute">
            Name<span className="hidden sm:inline"> your agent</span>
          </span>
          <span className="flex items-center font-mono text-[13px] text-ink">
            <input
              name="agent-name"
              value={raw}
              onChange={(e) => setRaw(toLocalPart(e.target.value))}
              spellCheck={false}
              autoComplete="off"
              maxLength={40}
              aria-label="Agent name (inbox local part)"
              className="w-[9ch] min-w-[4ch] border-b border-dashed border-ink/30 bg-transparent text-right outline-none focus:border-ink"
              style={{ width: `${Math.max(4, name.length + 1)}ch` }}
            />
            <span className="text-mute">@{HOSTED}</span>
          </span>
        </label>
        <div className="sheet relative z-10 mx-auto max-w-[620px] overflow-hidden rounded-xl">
          <div className="flex items-center justify-between border-b border-rule px-4 py-2.5 font-mono text-[11px] text-mute">
            <span>agent.ts</span>
            <span className="text-ink/35">preview</span>
          </div>
          <pre className="overflow-x-auto px-4 py-4 font-mono text-[12.5px] leading-6 text-ink-soft">
            <code>{highlight(flowCode(name), 'light')}</code>
          </pre>
        </div>

        {/* Connectors (desktop): trunk, bar, four drops. */}
        <div aria-hidden className="relative hidden h-16 md:block">
          <Connector className="absolute top-0 left-1/2 h-8" />
          <div className="absolute top-8 right-[12.5%] left-[12.5%] h-px bg-ink/15">
            <div className="flow-x absolute inset-0" />
          </div>
          {[12.5, 37.5, 62.5, 87.5].map((x) => (
            <div key={x} className="absolute top-8 h-8" style={{ left: `${x}%` }}>
              <span className="absolute -top-[3px] -left-[3px] h-[7px] w-[7px] rounded-full border border-ink/25 bg-paper" />
              <Connector className="h-8" />
            </div>
          ))}
        </div>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:mt-0 md:grid-cols-4">
          <WebhookTile />
          <SocketTile name={name} />
          <ThreadTile />
          <SearchTile />
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Agent demo
// ---------------------------------------------------------------------------

function AgentSection() {
  return (
    <section className="px-6 pb-36">
      <SectionTitle
        title={
          <>
            Watch an agent <span className="font-serif text-[1.1em] italic">handle</span> its mail.
          </>
        }
        sub="Create, receive, get pushed, reply. The whole loop is plain HTTP."
      />
      <div className="reveal mx-auto max-w-5xl">
        <HeroDemo />
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// How it works: step tabs, code, and the real response shape
// ---------------------------------------------------------------------------

type Lang = 'curl' | 'ts' | 'py';

const LANGS: { id: Lang; label: string }[] = [
  { id: 'curl', label: 'cURL' },
  { id: 'ts', label: 'TypeScript' },
  { id: 'py', label: 'Python' },
];

const STEPS: { id: string; title: string; status: string; response: string; code: Record<Lang, string> }[] = [
  {
    id: 'create',
    title: 'Create an inbox',
    status: '201 Created',
    response: `{
  "org_id": 42,
  "address": "support@k7f3.inboxkit.dev",
  "mode": "instant",
  "inbox": { "id": 118, ... }
}`,
    code: {
      curl: `curl -X POST ${API_HOST}/v1/inboxes \\
  -H "Authorization: Bearer $INBOXKIT_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"local_part": "support"}'`,
      ts: `const res = await fetch("${API_HOST}/v1/inboxes", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.INBOXKIT_API_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ local_part: "support" }),
});

const { address } = await res.json();`,
      py: `import os, requests

res = requests.post(
    "${API_HOST}/v1/inboxes",
    headers={"Authorization": f"Bearer {os.environ['INBOXKIT_API_KEY']}"},
    json={"local_part": "support"},
)

address = res.json()["address"]`,
    },
  },
  {
    id: 'listen',
    title: 'Get pushed new mail',
    status: 'secret shown once',
    response: `{
  "destination": {
    "id": 3,
    "url": "https://agent.example.com/hooks/mail",
    "events": ["message.received"],
    "inboxes": ["support@k7f3.inboxkit.dev"],
    "enabled": true,
    "secret": "<hmac-secret>"
  }
}`,
    code: {
      curl: `curl -X POST ${API_HOST}/v1/destinations \\
  -H "Authorization: Bearer $INBOXKIT_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "url": "https://agent.example.com/hooks/mail",
    "events": ["message.received"],
    "inboxes": ["support@k7f3.inboxkit.dev"]
  }'

# Verify each push: X-InboxKit-Signature: sha256=<hmac>`,
      ts: `await fetch("${API_HOST}/v1/destinations", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.INBOXKIT_API_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    url: "https://agent.example.com/hooks/mail",
    events: ["message.received"],
    inboxes: [address],
  }),
});

// Long-running agent? Subscribe at wss://api.inboxkit.dev/v1/ws`,
      py: `requests.post(
    "${API_HOST}/v1/destinations",
    headers={"Authorization": f"Bearer {os.environ['INBOXKIT_API_KEY']}"},
    json={
        "url": "https://agent.example.com/hooks/mail",
        "events": ["message.received"],
        "inboxes": [address],
    },
)

# Verify each push: X-InboxKit-Signature: sha256=<hmac>`,
    },
  },
  {
    id: 'reply',
    title: 'Reply in the thread',
    status: '200 OK',
    response: `{
  "success": true,
  "message_id": 1043,
  "thread_id": "t_9f2c",
  "from": "support@k7f3.inboxkit.dev",
  "external_sent": ["mira@northwind.example"]
}`,
    code: {
      curl: `curl -X POST ${API_HOST}/v1/inboxes/$ADDRESS/messages/$ID/reply \\
  -H "Authorization: Bearer $INBOXKIT_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"text": "Done — I refunded the duplicate charge."}'`,
      ts: `const url = \`${API_HOST}/v1/inboxes/\${address}/messages/\${event.message_id}/reply\`;

await fetch(url, {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${process.env.INBOXKIT_API_KEY}\`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify({ text: "Done — I refunded the duplicate charge." }),
});`,
      py: `url = f"${API_HOST}/v1/inboxes/{address}/messages/{event['message_id']}/reply"

requests.post(
    url,
    headers={"Authorization": f"Bearer {os.environ['INBOXKIT_API_KEY']}"},
    json={"text": "Done — I refunded the duplicate charge."},
)`,
    },
  },
];

function HowItWorks() {
  const [stepIndex, setStepIndex] = useState(0);
  const [lang, setLang] = useState<Lang>('ts');
  const step = STEPS[stepIndex] ?? STEPS[0];
  if (!step) return null;
  const code = step.code[lang];

  return (
    <section id="how" className="scroll-mt-16 px-6 pb-36">
      <SectionTitle
        title="Three calls to a working agent inbox."
        sub="Plain REST and JSON. Use it from any language, any framework, any agent runtime."
      />
      <div className="reveal sheet mx-auto max-w-6xl overflow-hidden rounded-2xl">
        <div className="flex flex-wrap items-stretch border-b border-rule">
          {STEPS.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStepIndex(i)}
              aria-pressed={i === stepIndex}
              className={`relative border-r border-rule px-5 py-3 text-left font-mono text-[12px] transition-colors ${
                i === stepIndex ? 'bg-paper-2/70 text-ink' : 'text-mute hover:text-ink'
              }`}
            >
              <span className="mr-2 text-ink/35">0{i + 1}</span>
              {s.id}
              {i === stepIndex && <span className="spectrum absolute inset-x-0 bottom-0 h-[2px]" />}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1 px-3 py-2">
            {LANGS.map((l) => (
              <button
                key={l.id}
                type="button"
                onClick={() => setLang(l.id)}
                aria-pressed={lang === l.id}
                className={`rounded-md px-2.5 py-1 font-mono text-[11.5px] transition-colors ${
                  lang === l.id ? 'bg-ink text-paper' : 'text-mute hover:text-ink'
                }`}
              >
                {l.label}
              </button>
            ))}
            <CopyButton text={code} />
          </div>
        </div>

        <div className="relative grid lg:grid-cols-[1.25fr_1fr]">
          <div className="min-w-0 border-b border-rule lg:border-r lg:border-b-0">
            <div className="px-6 pt-5 text-[15px] font-medium text-ink">{step.title}</div>
            <pre className="min-h-[330px] overflow-x-auto px-6 py-4 font-mono text-[12.5px] leading-6 text-ink-soft">
              <code key={`${stepIndex}-${lang}`} className="animate-fade-up block">
                {highlight(code, 'light', true)}
              </code>
            </pre>
          </div>

          <span className="glass absolute top-1/2 left-[55.55%] z-10 hidden h-9 w-9 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full lg:grid">
            <ArrowRight className="h-4 w-4 text-ink" aria-hidden />
          </span>

          <div className="min-w-0 bg-paper-2/40 p-3">
            <div className="screen h-full overflow-hidden rounded-xl">
              <div className="flex items-center justify-between border-b border-line px-4 py-2.5 font-mono text-[11px]">
                <span className="text-white/45">response</span>
                <span className="text-signal">{step.status}</span>
              </div>
              <pre className="overflow-x-auto p-4 font-mono text-[12px] leading-6 text-white/80">
                <code key={stepIndex} className="animate-fade-up block">
                  {highlight(step.response, 'dark')}
                </code>
              </pre>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 border-t border-rule px-6 py-3 font-mono text-[10.5px] tracking-[0.16em] uppercase">
          {STEPS.map((s, i) => (
            <span key={s.id} className="flex items-center gap-3">
              {i > 0 && <span className="text-ink/25">→</span>}
              <span className={i <= stepIndex ? 'text-ink' : 'text-ink/30'}>{s.id}</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

function Feature({
  icon,
  title,
  children,
  visual,
  delay = 0,
}: {
  icon: ReactNode;
  title: string;
  children: ReactNode;
  visual?: ReactNode;
  delay?: number;
}) {
  return (
    <div
      className="reveal sheet lift flex flex-col overflow-hidden rounded-2xl"
      style={{ transitionDelay: `${delay}ms` }}
    >
      {visual && <div className="border-b border-rule bg-paper-2/40">{visual}</div>}
      <div className="p-6">
        <div className="mb-2.5 flex items-center gap-2.5 text-ink">
          <span className="text-ink/70">{icon}</span>
          <h3 className="text-[15px] font-medium tracking-tight">{title}</h3>
        </div>
        <p className="text-[14px] leading-relaxed text-mute">{children}</p>
      </div>
    </div>
  );
}

const ROW = 'rounded-lg bg-field px-3 py-1.5 shadow-[0_0_0_1px_var(--color-rule)]';

function InboxesVisual() {
  const names = ['support', 'sales', 'agent-042', 'recruiter', 'signup-bot'];
  return (
    <div className="flex h-[200px] flex-col justify-center gap-1.5 p-5 font-mono text-[11.5px] [mask-image:linear-gradient(transparent,#000_20%,#000_80%,transparent)]">
      {names.map((n, i) => (
        <div key={n} className={`flex items-center gap-2.5 ${ROW}`} style={{ marginLeft: `${(i % 3) * 14}px` }}>
          <span className={`h-1.5 w-1.5 rounded-full ${i % 2 === 0 ? 'bg-leaf' : 'bg-ink/20'}`} />
          <span className="text-ink">{n}</span>
          <span className="truncate text-mute">@k7f3.inboxkit.dev</span>
        </div>
      ))}
    </div>
  );
}

function DomainVisual() {
  const records = [
    { type: 'MX', name: 'agents.acme.example', label: 'inbound' },
    { type: 'TXT', name: 'agents.acme.example', label: 'SPF' },
    { type: 'TXT', name: 'ik._domainkey', label: 'DKIM' },
    { type: 'TXT', name: '_dmarc', label: 'DMARC' },
  ];
  return (
    <div className="flex h-[200px] flex-col justify-center gap-1.5 p-5 font-mono text-[11px]">
      {records.map((r) => (
        <div key={r.label} className={`flex items-center gap-3 ${ROW}`}>
          <span className="w-7 text-code-get">{r.type}</span>
          <span className="min-w-0 flex-1 truncate text-mute">{r.name}</span>
          <span className="flex items-center gap-1 text-leaf">
            <Check className="h-3 w-3" aria-hidden />
            {r.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function MimeVisual() {
  const files = [
    { name: 'invoice-1042.pdf', size: '84 KB' },
    { name: 'receipt-march.png', size: '212 KB' },
    { name: 'contract-v3.docx', size: '46 KB' },
  ];
  return (
    <div className="flex h-[200px] flex-col justify-center gap-1.5 p-5 font-mono text-[11px]">
      <div className="mb-1 flex flex-wrap gap-1.5">
        {['text/plain', 'text/html', 'multipart/mixed'].map((t) => (
          <span key={t} className="rounded border border-rule-strong px-1.5 py-0.5 text-mute">
            {t}
          </span>
        ))}
      </div>
      {files.map((f) => (
        <div key={f.name} className={`flex items-center gap-2.5 ${ROW}`}>
          <Paperclip className="h-3 w-3 text-mute" aria-hidden />
          <span className="min-w-0 flex-1 truncate text-ink">{f.name}</span>
          <span className="text-mute">{f.size}</span>
        </div>
      ))}
    </div>
  );
}

function Primitives() {
  return (
    <section className="px-6 pb-36">
      <SectionTitle
        title={
          <>
            Not AI for your email.
            <br />
            <span className="font-serif text-[1.1em] italic">Email for your AI.</span>
          </>
        }
        sub="Everything an agent needs to own an address, hold a conversation, and act on what arrives."
      />
      <div className="mx-auto grid max-w-6xl gap-4 md:grid-cols-3">
        <Feature icon={<Inbox className="h-4 w-4" />} title="Inboxes on demand" visual={<InboxesVisual />}>
          One inbox per agent, per user, or per task. Each gets its own address and history on your hosted domain.
        </Feature>
        <Feature icon={<AtSign className="h-4 w-4" />} title="Your own domain" visual={<DomainVisual />} delay={80}>
          Start on inboxkit.dev, then move to your domain. We return the DNS records — or set them for you with Domain
          Connect.
        </Feature>
        <Feature
          icon={<Paperclip className="h-4 w-4" />}
          title="Full MIME + attachments"
          visual={<MimeVisual />}
          delay={160}
        >
          The raw message, parsed text and HTML, and every attachment. Send attachments back the same way.
        </Feature>
        <Feature icon={<FileSearch className="h-4 w-4" />} title="Extraction built in">
          Pull structured fields from a message or interpret an attachment with one call. Your agent acts, not parses.
        </Feature>
        <Feature icon={<Webhook className="h-4 w-4" />} title="Push, not polling" delay={80}>
          Signed webhooks for your backend, a WebSocket for long-running agents. Filter by inbox, test with a sample
          event.
        </Feature>
        <Feature icon={<KeyRound className="h-4 w-4" />} title="Scoped by design" delay={160}>
          Keys belong to an organization. Inboxes, events, and sockets never cross that line.
        </Feature>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Use cases: small dark scenes, like a gallery of examples
// ---------------------------------------------------------------------------

function Scene({ title, kicker, children, delay = 0 }: { title: string; kicker: string; children: ReactNode; delay?: number }) {
  return (
    <div className="reveal sheet lift overflow-hidden rounded-2xl" style={{ transitionDelay: `${delay}ms` }}>
      <div className="screen relative m-1.5 h-[190px] overflow-hidden rounded-xl font-mono text-[11px] text-white/55">{children}</div>
      <div className="px-5 pt-3 pb-5">
        <div className="text-[15px] font-medium tracking-tight text-ink">{title}</div>
        <div className="mt-1 text-[13px] leading-relaxed text-mute">{kicker}</div>
      </div>
    </div>
  );
}

/** Stagger delay for `.seq` items; they play once when their card is revealed. */
const seq = (delay: number) => ({ '--d': `${delay}s` }) as CSSProperties;

function UseCases() {
  return (
    <section className="px-6 pb-36">
      <SectionTitle
        title={
          <>
            What agents do <span className="font-serif text-[1.1em] italic">with an inbox.</span>
          </>
        }
        sub="Anything a person does with email, an agent can now do too — without borrowing someone's account."
      />
      <div className="mx-auto grid max-w-6xl gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Scene title="Sign up for things" kicker="Receive the verification email, read the code, finish the form.">
          <div className="absolute inset-0 flex flex-col justify-center gap-3 px-4">
            <div className="text-white/40">From: no-reply@app.example</div>
            <div className="text-white/75">Your verification code</div>
            <div className="text-[26px] tracking-[0.25em] text-signal seq" style={seq(0.4)}>
              482 193
            </div>
            <div className="text-white/35 seq" style={seq(0.9)}>
              → code entered · account created
            </div>
          </div>
        </Scene>
        <Scene title="Run a support desk" kicker="Answer every ticket in its own thread, hand off to a human when needed." delay={80}>
          <div className="absolute inset-0 flex flex-col justify-center gap-2 px-4">
            {[
              ['Refund for order 88-311', 'replied', 'text-signal'],
              ['Can I change my plan?', 'replied', 'text-signal'],
              ['Legal question about data', 'to human', 'text-syntax-fn'],
              ['Login link not arriving', 'drafting…', 'text-white/45'],
            ].map(([subject, state, cls], i) => (
              <div
                key={subject}
                className="flex items-center gap-2 rounded-md border border-line bg-white/[0.03] px-2 py-1.5 seq" style={seq(0.2 + i * 0.3)}
              >
                <span className="min-w-0 flex-1 truncate text-white/70">{subject}</span>
                <span className={cls}>{state}</span>
              </div>
            ))}
          </div>
        </Scene>
        <Scene title="Process invoices" kicker="Pull amounts and dates out of incoming mail with /extract." delay={160}>
          <div className="absolute inset-0 flex flex-col justify-center gap-2 px-4">
            <div className="flex items-center gap-2 text-white/70">
              <Paperclip className="h-3 w-3 text-white/40" aria-hidden />
              invoice-1042.pdf
            </div>
            <div className="rounded-md border border-line bg-white/[0.03] p-2.5 leading-5 seq" style={seq(0.5)}>
              {'{'}
              <div className="pl-3">
                <span className="text-syntax-key">"amount"</span>: <span className="text-syntax-str">"$1,240.00"</span>,
              </div>
              <div className="pl-3">
                <span className="text-syntax-key">"due_date"</span>: <span className="text-syntax-str">"10/31/2026"</span>
              </div>
              {'}'}
            </div>
          </div>
        </Scene>
        <Scene title="Book meetings" kicker="Trade a few emails, agree on a time, confirm in the same thread." delay={240}>
          <div className="absolute inset-0 flex flex-col justify-center gap-2 px-4 font-sans text-[11.5px]">
            <div className="max-w-[88%] rounded-lg rounded-bl-sm bg-white/[0.07] px-2.5 py-1.5 text-white/75 seq" style={seq(0.2)}>
              Can we meet next week?
            </div>
            <div
              className="ml-auto max-w-[88%] rounded-lg rounded-br-sm bg-signal/15 px-2.5 py-1.5 text-signal seq" style={seq(0.7)}
            >
              Thursday 14:00 or 16:00?
            </div>
            <div className="max-w-[88%] rounded-lg rounded-bl-sm bg-white/[0.07] px-2.5 py-1.5 text-white/75 seq" style={seq(1.2)}>
              16:00 works.
            </div>
          </div>
        </Scene>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// API, pricing, FAQ, CTA, footer
// ---------------------------------------------------------------------------

const ENDPOINTS: { method: 'GET' | 'POST'; path: string; desc: string }[] = [
  { method: 'POST', path: '/v1/inboxes', desc: 'Create an inbox' },
  { method: 'GET', path: '/v1/inboxes/:address/messages', desc: 'List messages' },
  { method: 'GET', path: '/v1/inboxes/:address/threads', desc: 'List threads' },
  { method: 'POST', path: '/v1/inboxes/:address/send', desc: 'Send an email' },
  { method: 'POST', path: '/v1/inboxes/:address/messages/:id/reply', desc: 'Reply in thread' },
  { method: 'POST', path: '/v1/destinations', desc: 'Push new mail to a URL' },
  { method: 'GET', path: '/v1/ws', desc: 'Realtime WebSocket' },
  { method: 'POST', path: '/v1/domains', desc: 'Add a custom domain' },
  { method: 'POST', path: '/v1/search', desc: 'Semantic search' },
  { method: 'POST', path: '/v1/messages/:id/extract', desc: 'Extract fields' },
];

function ApiSurface({ pushGuideUrl }: { pushGuideUrl: string }) {
  return (
    <section id="api" className="scroll-mt-20 px-6 pb-36">
      <div className="mx-auto grid max-w-6xl items-start gap-14 lg:grid-cols-[0.8fr_1.2fr]">
        <div className="reveal lg:sticky lg:top-28">
          <h2 className="text-balance text-[42px] leading-[1.05] tracking-[-0.035em] text-ink">
            A small API you can hold in your head.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-mute">
            Bearer-token auth, predictable resources, JSON in and out. Your agent — or your coding assistant — learns
            it in one read.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <GlassButton href={pushGuideUrl}>
              Push delivery guide
              <ArrowRight className="h-4 w-4" aria-hidden />
            </GlassButton>
          </div>
        </div>

        <div className="reveal sheet overflow-hidden rounded-2xl">
          <div className="flex items-center justify-between border-b border-rule px-5 py-3 font-mono text-[11px] text-mute">
            <span>api.inboxkit.dev</span>
            <span>Authorization: Bearer …</span>
          </div>
          {ENDPOINTS.map((e, i) => (
            <div
              key={e.path + e.method}
              className={`group flex items-center gap-4 px-5 py-3 transition-colors hover:bg-paper-2/60 ${i > 0 ? 'border-t border-rule' : ''}`}
            >
              <span
                className={`w-11 shrink-0 font-mono text-[11px] font-medium ${e.method === 'GET' ? 'text-code-get' : 'text-leaf'}`}
              >
                {e.method}
              </span>
              <span className="min-w-0 flex-1 truncate font-mono text-[12.5px] text-ink">{e.path}</span>
              <span className="hidden shrink-0 text-[13px] text-mute transition-colors group-hover:text-ink sm:block">
                {e.desc}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function Pricing({ requestAccessUrl }: { requestAccessUrl: string }) {
  const developer = PLAN_CONFIGS.developer;
  const startup = PLAN_CONFIGS.startup;
  const plans = [
    {
      name: developer.name,
      price: `$${developer.monthlyPrice}`,
      unit: '/month',
      note: developer.annualPrice ? `$${Math.round(developer.annualPrice / 12)}/mo billed annually` : '',
      blurb: 'For one agent in production.',
      features: [
        '14-day trial',
        `${developer.quotas.sendLimit.toLocaleString()} sends / month`,
        `${developer.quotas.receiveLimit.toLocaleString()} receives / month`,
        `${developer.quotas.inboxLimit} inboxes`,
        'Email support',
      ],
      featured: false,
    },
    {
      name: startup.name,
      price: `$${startup.monthlyPrice}`,
      unit: '/month',
      note: startup.annualPrice ? `$${Math.round(startup.annualPrice / 12)}/mo billed annually` : '',
      blurb: 'For a fleet of agents on your domain.',
      features: [
        `${startup.quotas.sendLimit.toLocaleString()} sends / month`,
        `${startup.quotas.receiveLimit.toLocaleString()} receives / month`,
        `${startup.quotas.inboxLimit} inboxes`,
        'Custom domains',
        'Priority support',
      ],
      featured: true,
    },
    {
      name: 'Enterprise',
      price: 'Custom',
      unit: '',
      note: 'Volume pricing',
      blurb: 'For platforms that give every user an inbox.',
      features: ['Unlimited sends', 'Unlimited receives', 'Unlimited inboxes', 'Dedicated support', 'Custom SLA'],
      featured: false,
    },
  ];

  return (
    <section id="pricing" className="scroll-mt-16 px-6 pb-36">
      <SectionTitle title="Simple plans. Scale when you do." />
      <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3 md:gap-4">
        {plans.map((p, i) => (
          <div
            key={p.name}
            className={`reveal relative flex flex-col rounded-2xl p-7 ${p.featured ? 'screen text-white' : 'sheet'}`}
            style={{ transitionDelay: `${i * 80}ms` }}
          >
            {p.featured && (
              <>
                <span className="spectrum absolute inset-x-8 -top-px h-px" />
                <span className="spectrum absolute inset-x-16 -top-3 h-6 opacity-40 blur-xl" />
              </>
            )}
            <div
              className={`font-mono text-[11.5px] tracking-[0.16em] uppercase ${p.featured ? 'text-white/55' : 'text-mute'}`}
            >
              {p.name}
              {p.featured && <span className="spectrum-text ml-2">· recommended</span>}
            </div>
            <div className="mt-5 flex items-baseline gap-1">
              <span className="text-[52px] leading-none tracking-[-0.04em]">{p.price}</span>
              <span className={`text-sm ${p.featured ? 'text-white/50' : 'text-mute'}`}>{p.unit}</span>
            </div>
            <div className={`mt-2 h-4 text-xs ${p.featured ? 'text-white/40' : 'text-mute'}`}>{p.note}</div>
            <p className={`mt-5 text-sm ${p.featured ? 'text-white/70' : 'text-ink-soft'}`}>{p.blurb}</p>
            <ul
              className={`mt-6 flex-1 space-y-3 border-t pt-6 text-sm ${p.featured ? 'border-line text-white/85' : 'border-rule text-ink-soft'}`}
            >
              {p.features.map((f) => (
                <li key={f} className="flex items-center gap-3">
                  <Check className={`h-4 w-4 shrink-0 ${p.featured ? 'text-signal' : 'text-leaf'}`} aria-hidden />
                  {f}
                </li>
              ))}
            </ul>
            <a
              href={requestAccessUrl}
              onClick={() => track('cta_click', { label: `pricing_${p.name.toLowerCase()}` })}
              className={`mt-8 rounded-full px-4 py-2.5 text-center text-sm font-medium transition-transform active:scale-[0.98] ${
                p.featured ? 'bg-white text-ink-950 hover:bg-white/90' : 'ink-btn'
              }`}
            >
              {p.name === 'Enterprise' ? 'Talk to us' : 'Request access'}
            </a>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Inline code inside prose. */
function C({ children }: { children: ReactNode }) {
  return (
    <code className="rounded-md border border-rule bg-paper-2/70 px-1.5 py-0.5 font-mono text-[0.86em] text-ink">
      {children}
    </code>
  );
}

const FAQS: { q: string; a: ReactNode }[] = [
  {
    q: 'Do I need my own domain?',
    a: (
      <>
        No. Every account gets a hosted <C>inboxkit.dev</C> domain, so your first inbox works in one API call. Add your
        own domain when you are ready — we give you the DNS records, or set them for you with Domain Connect.
      </>
    ),
  },
  {
    q: 'How does my agent find out about new mail?',
    a: (
      <>
        Register a destination for <C>message.received</C>, or hold a WebSocket open at <C>/v1/ws</C>. Each push carries
        an <C>X-InboxKit-Signature</C> header (HMAC-SHA256), so you can verify it came from us.
      </>
    ),
  },
  {
    q: 'Can my agent send new mail, not only reply?',
    a: (
      <>
        Yes. <C>POST /v1/inboxes/:address/send</C> sends a new message with text, HTML, and attachments. Replies go
        through <C>/messages/:id/reply</C> so they stay in the thread.
      </>
    ),
  },
  {
    q: 'Will replies thread correctly for the person on the other side?',
    a: (
      <>
        Yes. Replies set <C>In-Reply-To</C> and <C>References</C> headers, so mail clients group them with the original
        message.
      </>
    ),
  },
  {
    q: 'Can my agent read attachments?',
    a: (
      <>
        Yes. You get the full MIME message and every attachment. There are also endpoints to extract structured data
        from a message and to interpret an attachment.
      </>
    ),
  },
  {
    q: 'Is there an SDK?',
    a: (
      <>
        Not today. The API is plain REST and JSON with a Bearer key, so any HTTP client works — <C>fetch</C>,{' '}
        <C>requests</C>, or <C>curl</C>.
      </>
    ),
  },
  {
    q: 'Why is access by request?',
    a: (
      <>
        InboxKit is in private beta. We onboard teams in small batches so that deliverability stays high for every
        sender on the platform.
      </>
    ),
  },
];

function FaqItem({
  index,
  q,
  a,
  open,
  onToggle,
}: {
  index: number;
  q: string;
  a: ReactNode;
  open: boolean;
  onToggle: () => void;
}) {
  const id = `faq-${index}`;
  return (
    <div className="border-t border-rule last:border-b">
      <h3>
        <button
          type="button"
          id={`${id}-q`}
          aria-expanded={open}
          aria-controls={`${id}-a`}
          onClick={onToggle}
          className="group flex w-full items-center gap-5 py-6 text-left"
        >
          <span className="w-7 shrink-0 font-mono text-[12px] text-mute tabular-nums">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span
            className={`flex-1 text-[19px] leading-snug tracking-[-0.01em] transition-colors sm:text-[21px] ${
              open ? 'text-ink' : 'text-ink-soft group-hover:text-ink'
            }`}
          >
            {q}
          </span>
          <span
            aria-hidden
            className={`glass grid h-9 w-9 shrink-0 place-items-center rounded-full transition-transform duration-500 ease-[cubic-bezier(0.2,0.7,0.2,1)] ${
              open ? 'rotate-45' : 'group-hover:rotate-90'
            }`}
          >
            <Plus className="h-4 w-4 text-ink" />
          </span>
        </button>
      </h3>
      <div
        id={`${id}-a`}
        role="region"
        aria-labelledby={`${id}-q`}
        className={`grid transition-[grid-template-rows,opacity] duration-500 ease-[cubic-bezier(0.2,0.7,0.2,1)] ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <p className="max-w-2xl pb-7 pl-12 text-[15.5px] leading-[1.7] text-ink-soft sm:pr-14">{a}</p>
        </div>
      </div>
    </div>
  );
}

function Faq({ requestAccessUrl }: { requestAccessUrl: string }) {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="scroll-mt-20 px-6 pb-36">
      <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-[0.8fr_1.2fr] lg:gap-20">
        <div className="reveal lg:sticky lg:top-28 lg:self-start">
          <h2 className="font-serif text-[56px] leading-[0.95] tracking-[-0.02em] text-ink sm:text-[72px]">
            Questions,
            <br />
            <span className="italic">answered.</span>
          </h2>
          <p className="mt-6 max-w-sm text-lg leading-relaxed text-ink-soft">
            The short version of how InboxKit works. Something missing? Ask us when you request access.
          </p>
          <div className="mt-8">
            <GlassButton href={requestAccessUrl}>
              Ask us anything
              <ArrowRight className="h-4 w-4" aria-hidden />
            </GlassButton>
          </div>
        </div>

        <div className="reveal">
          {FAQS.map((f, i) => (
            <FaqItem
              key={f.q}
              index={i}
              q={f.q}
              a={f.a}
              open={open === i}
              onToggle={() => setOpen(open === i ? null : i)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ requestAccessUrl }: { requestAccessUrl: string }) {
  return (
    <section className="paper-light relative overflow-hidden px-6 pt-32 pb-40">
      <div className="reveal relative mx-auto max-w-3xl text-center">
        <div aria-hidden className="spectrum mx-auto mb-12 h-px w-24 opacity-80" />
        <h2 className="font-serif text-[64px] leading-[0.95] tracking-[-0.02em] text-ink sm:text-[104px]">
          Give your agent
          <br />
          <span className="italic">an address.</span>
        </h2>
        <p className="mt-8 text-lg text-ink-soft">Tell us what you are building. We will get you set up.</p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
          <InkButton href={requestAccessUrl} label="footer">
            Request access
          </InkButton>
          <GlassButton href="#api">Explore the API</GlassButton>
        </div>
      </div>
    </section>
  );
}

export default function App() {
  const urls = useSiteUrls();
  const requestAccessUrl = urls.requestAccess;
  useReveal();

  return (
    <div className="landing paper-grain min-h-screen font-sans">
      <SiteHeader
        homeUrl="/"
        links={[
          { label: 'Product', href: '#flow' },
          { label: 'How it works', href: '#how' },
          { label: 'Pricing', href: '#pricing' },
          { label: 'Docs', href: '#api' },
        ]}
        signInUrl={urls.app}
        cta={{ label: 'Request access', href: requestAccessUrl, onClick: () => track('cta_click', { label: 'nav' }) }}
      />
      <main>
        <Hero requestAccessUrl={requestAccessUrl} />
        <Manifesto />
        <Flow />
        <AgentSection />
        <HowItWorks />
        <Primitives />
        <UseCases />
        <ApiSurface pushGuideUrl={urls.pushGuide} />
        <Pricing requestAccessUrl={requestAccessUrl} />
        <Faq requestAccessUrl={requestAccessUrl} />
        <FinalCta requestAccessUrl={requestAccessUrl} />
      </main>
      <SiteFooter
        statusUrl={urls.status}
        columns={[
          {
            title: 'Product',
            links: [
              { label: 'Overview', href: '#flow' },
              { label: 'How it works', href: '#how' },
              { label: 'Pricing', href: '#pricing' },
            ],
          },
          {
            title: 'Developers',
            links: [
              { label: 'API', href: '#api' },
              { label: 'Push delivery guide', href: urls.pushGuide },
              { label: 'Status', href: urls.status },
            ],
          },
          {
            title: 'Account',
            links: [
              { label: 'Request access', href: requestAccessUrl },
              { label: 'Sign in', href: urls.app },
            ],
          },
        ]}
      />
    </div>
  );
}
